import { z } from 'zod';
import { PROBLEM_IDS, type ProblemId } from './problems';

// Structured analysis for the Problem Finder. `category`, `score` and `insights` keep the
// StandardAnalysis shape the email orchestrator reads; the rest is what the result page
// renders. Generated with generateObject (OpenAI strict mode), so every field is required
// and nothing uses string format validators.

export interface ProblemFinderAnalysisResponse {
  problemId: ProblemId;
  /** The problem label. Doubles as the StandardAnalysis `category`. */
  category: string;
  /** 0-100, how heavy the problem is right now. Kept for StandardAnalysis compatibility. */
  score: number;
  /** One sentence naming the problem, in the second person. */
  problemStatement: string;
  /** One sentence on what it is costing them, from their answers. */
  costLine: string;
  /** Two or three of the reader's own answers, quoted back. */
  echoes: string[];
  /** Why the problem keeps coming back, from what they tried and what happened. */
  whyItSticks: string;
  /** One step for this week, sized to the time they said they have. */
  thisWeek: {
    step: string;
    detail: string;
  };
  /** Short standalone takeaways, for the email orchestrator. */
  insights: string[];
}

export const ProblemFinderAnalysisResponseSchema = z.object({
  problemId: z
    .enum(PROBLEM_IDS)
    .describe('The problem id for the life area the reader picked in question 1.'),
  category: z.string().describe('The problem label for this problem id, copied exactly.'),
  score: z
    .number()
    .describe('0-100: how heavy this problem is right now, from duration and cost. Higher is heavier.'),
  problemStatement: z
    .string()
    .describe('One sentence, second person, naming the problem in the reader\'s own terms.'),
  costLine: z.string().describe('One sentence on what this is costing them, from their cost answers.'),
  echoes: z
    .array(z.string())
    .describe('Two or three of the reader\'s own answers, quoted close to verbatim, first person.'),
  whyItSticks: z
    .string()
    .describe('Two or three sentences on why the problem keeps coming back, from what they tried and what happened.'),
  thisWeek: z.object({
    step: z.string().describe('One concrete step for this week, one sentence.'),
    detail: z
      .string()
      .describe('One or two sentences: how long it takes and why it is the right first step.'),
  }),
  insights: z.array(z.string()).describe('Three short standalone takeaways.'),
}) satisfies z.ZodType<ProblemFinderAnalysisResponse>;

export function isProblemFinderAnalysis(value: unknown): value is ProblemFinderAnalysisResponse {
  return ProblemFinderAnalysisResponseSchema.safeParse(value).success;
}
